import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Stack,
  IconButton,
  OutlinedInput,
  InputAdornment,
  Box,
  useTheme,
  Tooltip,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { Search, Close as CloseIcon } from "@mui/icons-material";
import Header from "component/Header";
import { Links } from "utils/pageLinkNames";
import { getAllTemplates, updateTemplate, deleteTemplate } from "features/service/service.slice";
import DataGridTable from "component/DataGridTable";
import { Edit as EditIcon } from "@mui/icons-material";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { toast } from "react-toastify";
import FilterHeaderDropdown from "component/ui/FilterHeaderDropdown";
import TableSkeleton from "component/Loaders/TableSkeleton";

const Templates = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchValue, setSearchValue] = useState("");
  const [templateGridFilters, setTemplateGridFilters] = useState({
    vendor: "",
    machineModel: "",
    active: "",
  });
  const { allTemplates, appConstants } = useSelector((state) => state.service);
  const { mode } = useSelector((state) => state.globalState);
  const [templatesList, setTemplatesList] = useState([]);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    dispatch(getAllTemplates());
  }, [dispatch]);

  useEffect(() => {
    if (allTemplates.data.length && !allTemplates.isLoading && appConstants?.customers?.length) {
      const templatesWithDisplayFields = allTemplates.data.map((template) => {
        const vendorName =
          appConstants.customers.find((item) => item.id === parseInt(template.vendor))?.customerName || "Internal";
        const modelName =
          appConstants.machineModels.find((item) => item.id === parseInt(template.modelId))?.model?.trim() || "";
        return { ...template, vendorName, modelName };
      });
      setTemplatesList(templatesWithDisplayFields);
    }
  }, [allTemplates.data, allTemplates.isLoading, appConstants]);

  const getTemplatesMatchingFilters = ({ vendor = "", machineModel = "" } = {}) =>
    templatesList.filter((template) => {
      if (vendor && template.vendorName !== vendor) return false;
      if (machineModel && template.modelName !== machineModel) return false;
      return true;
    });

  const getActiveOptionsForTemplates = (templates) => {
    const activeValues = new Set();
    for (const template of templates) {
      activeValues.add(template.active ? "Yes" : "No");
    }
    return [...activeValues];
  };

  const vendorFilterOptions = useMemo(() => {
    const vendors = new Set();
    for (const template of templatesList) {
      if (template.vendorName) vendors.add(template.vendorName);
    }
    return [...vendors];
  }, [templatesList]);

  const machineModelFilterOptions = useMemo(() => {
    const models = new Set();
    for (const template of getTemplatesMatchingFilters({ vendor: templateGridFilters.vendor })) {
      if (template.modelName) models.add(template.modelName);
    }
    return [...models];
  }, [templatesList, templateGridFilters.vendor]);

  const activeFilterOptions = useMemo(
    () =>
      getActiveOptionsForTemplates(
        getTemplatesMatchingFilters({
          vendor: templateGridFilters.vendor,
          machineModel: templateGridFilters.machineModel,
        })
      ),
    [templatesList, templateGridFilters.vendor, templateGridFilters.machineModel]
  );

  const filteredTemplates = useMemo(() => {
    const searchLower = searchValue.toLowerCase();
    return templatesList
      .filter((item) => Object.values(item).join(" ").toLowerCase().includes(searchLower))
      .filter((item) => !templateGridFilters.vendor || item.vendorName === templateGridFilters.vendor)
      .filter((item) => !templateGridFilters.machineModel || item.modelName === templateGridFilters.machineModel)
      .filter((item) => {
        if (!templateGridFilters.active) return true;
        return (item.active ? "Yes" : "No") === templateGridFilters.active;
      });
  }, [templatesList, searchValue, templateGridFilters]);

  const handleVendorFilterChange = (value) => {
    setTemplateGridFilters((prev) => {
      let nextMachineModel = prev.machineModel;
      if (prev.machineModel && value) {
        const modelsForVendor = new Set(
          getTemplatesMatchingFilters({ vendor: value }).map((template) => template.modelName).filter(Boolean)
        );
        if (!modelsForVendor.has(prev.machineModel)) {
          nextMachineModel = "";
        }
      }

      let nextActive = prev.active;
      if (prev.active) {
        const activeForFilters = getActiveOptionsForTemplates(
          getTemplatesMatchingFilters({ vendor: value, machineModel: nextMachineModel })
        );
        if (!activeForFilters.includes(prev.active)) {
          nextActive = "";
        }
      }

      return { ...prev, vendor: value, machineModel: nextMachineModel, active: nextActive };
    });
  };

  const handleMachineModelFilterChange = (value) => {
    setTemplateGridFilters((prev) => {
      let nextActive = prev.active;
      if (prev.active) {
        const activeForFilters = getActiveOptionsForTemplates(
          getTemplatesMatchingFilters({ vendor: prev.vendor, machineModel: value })
        );
        if (!activeForFilters.includes(prev.active)) {
          nextActive = "";
        }
      }

      return { ...prev, machineModel: value, active: nextActive };
    });
  };
  const clearAllTemplatesFilters = () => {
    setTemplateGridFilters({
      vendor: "",
      machineModel: "",
      active: "",
    });
    setSearchValue("");
  };

  const handleOpenDialog = (template) => {
    setTemplateToDelete(template);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setTemplateToDelete(null);
  };

  const handleDeleteConfirmed = async () => {
    dispatch(deleteTemplate(templateToDelete.id)).then((deleteRes) => {
      if (deleteRes && deleteRes.error) return toast.error("Something went wrong!");
      if (deleteRes && deleteRes.payload && deleteRes.payload.status === 200) {
        dispatch(getAllTemplates()).then((res) => {
          toast.success("Template deleted successfully!");
          handleCloseDialog();
        });
      } else return toast.error("Something went wrong!");
    });
  };

  const tableGrids = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        minWidth: 60,
        flex: 0.4,
      },
      {
        field: "vendorName",
        headerName: "Vendor",
        minWidth: 120,
        flex: 0.4,
        renderHeader: () => (
          <FilterHeaderDropdown
            label="Vendor"
            value={templateGridFilters.vendor}
            options={vendorFilterOptions}
            onChange={handleVendorFilterChange}
          />
        ),
      },
      {
        field: "modelName",
        headerName: "Machine Model",
        minWidth: 140,
        flex: 0.4,
        renderHeader: () => (
          <FilterHeaderDropdown
            label="Machine Model"
            value={templateGridFilters.machineModel}
            options={machineModelFilterOptions}
            onChange={handleMachineModelFilterChange}
          />
        ),
      },
      {
        field: "active",
        headerName: "Active",
        minWidth: 100,
        flex: 0.4,
        valueGetter: (params) => (params.row[params.field] ? "Yes" : "No"),
        renderHeader: () => (
          <FilterHeaderDropdown
            label="Active"
            value={templateGridFilters.active}
            options={activeFilterOptions}
            onChange={(value) => setTemplateGridFilters({ ...templateGridFilters, active: value })}
          />
        ),
      },
      {
        minWidth: 60,
        flex: 0.2,
        sortable: false,
        filterable: false,
        renderCell: (args) => (
          <div>
            <IconButton
              sx={{ mr: 1 }}
              onClick={() => {
                navigate(`/${Links.CREATE_TEMPLATE}`, { state: { copyTemplate: true, copyTemplateId: args.row.id } });
              }}
            >
              <Tooltip title="Copy">
                <FileCopyIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </Tooltip>
            </IconButton>
            <IconButton sx={{ mr: 1 }} onClick={() => navigate(`/${Links.EDIT_TEMPLATE}/` + args.row.id)}>
              <Tooltip title="Edit">
                <EditIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </Tooltip>
            </IconButton>
            <IconButton sx={{ mr: 1.5 }} onClick={() => handleOpenDialog(args.row)}>
              <Tooltip title="Delete">
                <DeleteIcon sx={{ color: mode === "dark" ? "#fff" : theme.palette.primary[50] }} />
              </Tooltip>
            </IconButton>
          </div>
        ),
      },
    ],
    [templateGridFilters, vendorFilterOptions, machineModelFilterOptions, activeFilterOptions, mode, navigate, theme.palette.primary]
  );

  const onTmpUdate = async () => {
    // Code to be changed ===============================
    const testsections = [];
    let test = structuredClone(allTemplates.data);
    for (let tmp of test) {
      let tmptemplate = { ...tmp };
      for (let sss in tmptemplate.sections) {
        let tmpSection = tmptemplate.sections[sss];
        const randomNumber = Math.floor(Math.random() * (99999 - 10000 + 1)) + 10000;
        tmpSection.id = randomNumber + sss + "";
        const testtasks = [];
        for (let ttt in tmpSection.tasks) {
          const randomNumber2 = Math.floor(Math.random() * (99999 - 10000 + 1)) + 10000;
          let tmpSectionTask = tmpSection.tasks[ttt];
          tmpSectionTask.id = randomNumber2 + ttt + "";
          testtasks.push(tmpSectionTask);
        }
        tmpSection.tasks = testtasks;
        testsections.push(tmpSection);
      }
      tmptemplate.sections = testsections;
      tmptemplate.templateId = tmptemplate.id;

      const formDataToPost = new FormData();
      formDataToPost.append("template", JSON.stringify(tmptemplate));
      if (tmptemplate.id == 97) {
        console.log("tmp--", tmptemplate);
        dispatch(updateTemplate(formDataToPost)).then((templateResp) => {
          console.log("templateResp---", templateResp);
        });
      }
    }
    // formData.sections = testsections;
    // till here ===============================
  };

  return (
    <div>
      <Stack sx={{ ml: 2, mt: 2, mb: 1 }}>
        <Header titles={["Templates"]} />
      </Stack>
      {/* <Button onClick={() => onTmpUdate()}>onTmpUdate</Button> */}
      <Box sx={{ mx: 2 }}>
        <Stack direction={"row"} gap={1} alignItems={"center"} my={2}>
          <Button
            startIcon={<AddIcon />}
            sx={{ height: "self" }}
            autoCapitalize="none"
            variant="contained"
            onClick={() => navigate(`/${Links.CREATE_TEMPLATE}`)}
          >
            ADD
          </Button>
          <OutlinedInput
            id="outlined-adornment-password"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Search..."
            endAdornment={
              <InputAdornment position="end">
                <IconButton aria-label="toggle password visibility" edge="end">
                  <Search />
                </IconButton>
              </InputAdornment>
            }
            sx={{ minWidth: 300, maxWidth: 600, height: 35 }}
          />
          <Button
            startIcon={<CloseIcon />}
            sx={{ height: "self" }}
            autoCapitalize="none"
            variant="contained"
            onClick={clearAllTemplatesFilters}
          >
            Clear All
          </Button>
        </Stack>
        {allTemplates.isLoading ? (
          <TableSkeleton rows={8} columns={tableGrids.length} />
        ) : (
          <DataGridTable rowId={"id"} data={filteredTemplates} columnAttributes={tableGrids} />
        )}
      </Box>
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography variant="body1">Are you sure you want to delete the template "{templateToDelete?.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            No
          </Button>
          <Button onClick={handleDeleteConfirmed} color="primary">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Templates;
